'use client';

import type { INavListItem } from '@/api/types/public';
import { useLayoutEffect, useRef, useState } from 'react';
import LensProcessNavPc from './LensProcessNav.pc';
import {
  canDisplayPriorityModule,
  RESPONSIVE_LENS_PROCESS_WIDTH,
} from './navigationLayoutPriority';
import styles from './index.module.scss';
import TextNav from './TextNav';

interface TextLensNavigationLayoutProps {
  textNavItems: INavListItem[];
  lensProcessItems: INavListItem[];
  firstLevelIndex: number;
  firstLevelTitle: string;
  isDarkMode: boolean;
  isActive: boolean;
}

const RESPONSIVE_DESKTOP_QUERY = '(min-width: 1281px) and (max-width: 1919px)';
const LENS_PROCESS_GAP = 16;

const TextLensNavigationLayout = ({
  textNavItems,
  lensProcessItems,
  firstLevelIndex,
  firstLevelTitle,
  isDarkMode,
  isActive,
}: TextLensNavigationLayoutProps) => {
  const layoutRef = useRef<HTMLDivElement>(null);
  const textNavigationRef = useRef<HTMLDivElement>(null);
  const [showLensProcess, setShowLensProcess] = useState(true);
  const textNavItemCount = textNavItems.length;
  const lensProcessCount = lensProcessItems.length;

  useLayoutEffect(() => {
    if (!isActive) return;

    const updateLensProcessVisibility = () => {
      const layout = layoutRef.current;
      const textNavigation = textNavigationRef.current;
      if (!layout || !textNavigation) return;

      const isResponsiveDesktop = window.matchMedia(RESPONSIVE_DESKTOP_QUERY).matches;
      const lensProcessWidth =
        lensProcessCount * RESPONSIVE_LENS_PROCESS_WIDTH +
        Math.max(0, lensProcessCount - 1) * LENS_PROCESS_GAP;
      const nextVisibility =
        !isResponsiveDesktop ||
        canDisplayPriorityModule({
          availableWidth: layout.clientWidth,
          higherPriorityWidth: textNavigation.scrollWidth,
          moduleWidth: lensProcessWidth,
        });

      setShowLensProcess((currentVisibility) =>
        currentVisibility === nextVisibility ? currentVisibility : nextVisibility,
      );
    };

    updateLensProcessVisibility();

    const resizeObserver = new ResizeObserver(updateLensProcessVisibility);
    if (layoutRef.current) resizeObserver.observe(layoutRef.current);
    if (textNavigationRef.current) resizeObserver.observe(textNavigationRef.current);

    return () => resizeObserver.disconnect();
  }, [isActive, lensProcessCount, textNavItemCount]);

  return (
    <div ref={layoutRef} className={styles.textLensNavigationLayout}>
      <div
        ref={textNavigationRef}
        className={`flex items-start ${styles.textLensNavigationTextContainer}`}
      >
        {textNavItems.map((item, index) => (
          <div
            key={`${item.id}-${item.template_id}`}
            className={styles.responsiveTextNavigationColumn}
          >
            <TextNav
              item={item}
              firstLevelIndex={firstLevelIndex}
              firstLevelTitle={firstLevelTitle}
              secondLevelIndex={index}
              isLast={index === textNavItems.length - 1}
              isDarkMode={isDarkMode}
            />
          </div>
        ))}
      </div>

      {showLensProcess ? (
        <div
          className={`ml-auto flex shrink-0 items-stretch ${styles.lensProcessContainer} ${styles.textLensNavigationLensContainer}`}
        >
          {lensProcessItems.map((item) => (
            <LensProcessNavPc key={`${item.id}-${item.template_id}`} isDarkMode={isDarkMode} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default TextLensNavigationLayout;
